import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import { useSelector } from 'react-redux';
import { getSizeClass, type LayoutSizeClass } from './layoutBreakpoints';
import { getEffectiveSidebarNav } from '../utils/layoutUtils';
import { selectTabletPortraitNavLayout } from '../redux/SettingsSlice';

export interface LayoutSize {
  width: number;
  height: number;
  sizeClass: LayoutSizeClass;
  isLandscape: boolean;
  isPortrait: boolean;
  orientation: 'portrait' | 'landscape';
  /** Sidebar navigation (web wide, or native tablet landscape). */
  useSidebarNav: boolean;
  /** Web-style layout: web platform, or native when {@link useSidebarNav}. */
  useWebLayout: boolean;
  /** Expanded/wide layout; same predicate as {@link useWebLayout} in current design. */
  useExpandedLayout: boolean;
}

const LayoutSizeContext = createContext<LayoutSize | undefined>(undefined);

export const LayoutSizeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { width, height } = useWindowDimensions();
  const tabletPortraitNavLayout = useSelector(selectTabletPortraitNavLayout);
  const value = useMemo<LayoutSize>(() => {
    const sizeClass = getSizeClass(width);
    const isLandscape = width > height;
    const useSidebarNav = getEffectiveSidebarNav(
      width,
      height,
      tabletPortraitNavLayout,
      Platform.OS,
    );
    const useWebLayout = Platform.OS === 'web' || useSidebarNav;
    const useExpandedLayout = useWebLayout;
    return {
      width,
      height,
      sizeClass,
      isLandscape,
      isPortrait: !isLandscape,
      orientation: isLandscape ? 'landscape' : 'portrait',
      useSidebarNav,
      useWebLayout,
      useExpandedLayout,
    };
  }, [width, height, tabletPortraitNavLayout]);

  return (
    <LayoutSizeContext.Provider value={value}>
      {children}
    </LayoutSizeContext.Provider>
  );
};

export const useLayoutSize = (): LayoutSize => {
  const context = useContext(LayoutSizeContext);
  if (context === undefined) {
    throw new Error('useLayoutSize must be used within a LayoutSizeProvider');
  }
  return context;
};
